import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { User } from '@app/core/interfaces';
import { BehaviorSubject, catchError, map, Observable, shareReplay, throwError } from 'rxjs';
import { ApiService, StorageService } from '@app/core/services';
import { Router } from '@angular/router';
import { jwtDecode } from "jwt-decode"
import moment from 'moment';

export interface TokenResult {
  access_token: string;
  token_type: string;
  full_name: string;
  is_superuser: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService extends ApiService {
  private _isConnected: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public isConnected$: Observable<boolean> = this._isConnected.asObservable().pipe(shareReplay());

  private _isAdmin: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public isAdmin$: Observable<boolean> = this._isAdmin.asObservable().pipe(shareReplay());

  private httpClient: HttpClient;

  constructor(
    private storageService: StorageService,
    private httpBackend: HttpBackend,
    private router: Router
  ) {
    super()
    this.httpClient = new HttpClient(this.httpBackend)
    this.updateIsConnected()
    this.updateIsAdmin()
  }

  public login(user: Partial<User>): Observable<TokenResult> {
    return this.httpClient.post<TokenResult>(`${this.apiUrl}/auth/login-json`, {email: user.email, password: user.password})
  }

  public autoLogin(): Observable<boolean> {
    const user: User = this.storageService.user
    return this.login(user).pipe(
      catchError((err) => {
        this.logout()
        return throwError(() => err)
      }),
      map((response: TokenResult) => {
        if (response && response.access_token) {
          this.storageService.token = response.access_token
          return true
        }
        return false
      })
    )
  }

  public logout(redirect: boolean = true): void {
    this.storageService.clear()
    this.updateIsConnected()
    if (redirect) {
      this.router.navigate(['auth'])
    }
  }

  public isAuthenticated(): boolean {
    const timestamp = moment().unix()
    if (this.storageService.token) {
      try {
        const token = <any>jwtDecode(this.storageService.token);
        if (token.hasOwnProperty('exp')) {
          return timestamp < token.exp
        }
        return false
      } catch (e) {
        return false
      }
    }
    return false
  }

  public loginHandler(user: User, result: TokenResult) {
    this.storageService.token = result.access_token;
    this.storageService.userEmail = user.email || '';
    this.storageService.userPassword = user.password || '';
    this.storageService.userName = result.full_name;
    this.storageService.userIsSuperuser = result.is_superuser;
    this.updateIsConnected();
    this.updateIsAdmin();
  }

  public updateIsConnected() {
    this._isConnected.next(this.isAuthenticated());
  }

  public updateIsAdmin() {
    this._isAdmin.next(this.storageService.userIsSuperuser === true);
  }

  public isAdmin(): boolean {
      return this.storageService.userIsSuperuser === true;
  }
}
